import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@material-ui/core';
import { appConfig } from 'brand-config';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import triangleAlertIcon from 'core/assets/svg/triangle-alert.svg';
import useStyles from 'offer/components/offer-redirect-interstitial/offer-redirect-interstitial.styles';

/**
 * "Go to rewritten policy" prompt (#36661). A rewrite flat-cancels the original (CNRW) and clones it
 * onto a new policy id, so the original on its own reads as a plain cancellation. rewritePolicy
 * stamps `rewrittenToPolicyId` on the original; opening it asks whether to go to the rewrite.
 *
 * Reuses the offer redirect's confirmation dialog (#30994/#35043) but never redirects on its own —
 * staff opening the original often mean to look at the cancelled term. "Stay" lasts for the current
 * page view of this policy only. Brand-gated (`featureFlags.policyRewrittenRedirect`); on for every
 * brand today.
 */

/**
 * The newest policy in the rewrite chain starting at `policy` (A → B → C resolves to C), or null when
 * it was never rewritten. Hops past the direct rewrite only through policies on the loaded account.
 */
export const findLatestRewriteId = (policy, policies = []) => {
  const visitedPolicyIds = new Set([policy?.id]);
  let latestRewriteId = policy?.rewrittenToPolicyId || null;
  while (latestRewriteId && !visitedPolicyIds.has(latestRewriteId)) {
    visitedPolicyIds.add(latestRewriteId);
    const nextRewriteId = policies.find(({ id }) => id === latestRewriteId)?.rewrittenToPolicyId;
    if (!nextRewriteId || visitedPolicyIds.has(nextRewriteId)) {
      break;
    }
    latestRewriteId = nextRewriteId;
  }
  return latestRewriteId;
};

const PolicyRewrittenModal = ({ policy, policies, accountId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [stayedOnPolicyId, setStayedOnPolicyId] = useState(null);

  const latestRewriteId = findLatestRewriteId(policy, policies);
  if (!appConfig?.featureFlags?.policyRewrittenRedirect || !latestRewriteId || stayedOnPolicyId === policy.id) {
    return null;
  }

  return (
    <Dialog open classes={{ paper: classes.confirmPaper }}>
      <div className={classes.confirmContent}>
        <img src={triangleAlertIcon} className={classes.warningIcon} alt="Warning" />
        <div className={classes.confirmCopy}>
          <Label type="greenBig">This policy was rewritten</Label>
          <Label type="modalBody" className={classes.confirmBody}>
            {`This policy was rewritten as policy ${latestRewriteId}. Would you like to go to the rewritten policy?`}
          </Label>
        </div>
      </div>
      <div className={classes.confirmActions}>
        <Button onClick={() => setStayedOnPolicyId(policy.id)} variant="contained" color="secondary">
          Stay on this policy
        </Button>
        <Button
          onClick={() => history.push(`/customer/${accountId}/policy/${latestRewriteId}`)}
          variant="contained"
          color="primary"
        >
          Go to rewritten policy
        </Button>
      </div>
    </Dialog>
  );
};

PolicyRewrittenModal.propTypes = {
  policy: PropTypes.object.isRequired,
  policies: PropTypes.array,
  accountId: PropTypes.string.isRequired
};

PolicyRewrittenModal.defaultProps = {
  policies: []
};

export default PolicyRewrittenModal;
